<!-- eslint-disable vuejs-accessibility/label-has-for -->
<script setup lang="ts">
import SegmentationModule from '@src/components/segmentation/SegmentationModule.vue';
import { useAppSettings } from '@src/composables/useAppSettings';
import { computed, ref, useTemplateRef } from 'vue';

import LoadSaveAnalysisStateDialog from '@/src/components/LoadSaveAnalysisStateDialog.vue';
import { useViewportActions } from '@/src/components/pixel-overlay/useViewportActions';
import { useAlert } from '@/src/composables/useAlert';
import {
  ContourDefinitions,
  ContourLabel,
  ContourLabelToIcon,
} from '@/src/store/contours/contour-definitions';
import { ViewportToolMode } from '@/src/store/contours/types/tool-modes-type';
import { useViewportStateStore } from '@/src/store/contours/viewport-state-store';
import { useCurrentStudyStore } from '@/src/store/dicom-web/current-study-store';
import { useAppState } from '@/src/store/ocg-app-state';
import { useGeneralPurposeOverlayManager } from '@/src/store/overlay-management/overlay-manager';

import OCGModule from '../../ocg-backend/OCGModule.vue';
import ToolButton from '../../ToolButton.vue';

// Refs and stores
const viewportActions = useViewportActions();
const viewportStateStore = useViewportStateStore();
const currentStudyStore = useCurrentStudyStore();
const alert = useAlert();
const {
  internalEndoOffset,
  internalEpiOffset,
  offsetOptions,
  defaultEpiOffset,
  defaultEndoOffset,
  applyEndoEpiOffsets,
  resetAppOffsets,
} = useAppSettings();

const appState = useAppState();
const ocgModule = ref<InstanceType<typeof OCGModule> | null>(null);
const segModule = ref<InstanceType<typeof SegmentationModule> | null>(null);
const loadSaveAnalysisStateDialog = useTemplateRef<
  InstanceType<typeof LoadSaveAnalysisStateDialog>
>('loadSaveAnalysisStateDialog');
const showOcgDialog = ref(false);
const overlayManager = useGeneralPurposeOverlayManager();
const generalPurposeOverlayState
  = overlayManager.getGeneralPurposeOverlayState();
const isSavingAnalysisState = ref(false);

const openCategories = ref({
  aiTools: false,
  contourTools: false,
  cameraControl: false,
  offsetTools: false,
});

function stepEpi(delta: number) {
  const current = internalEpiOffset.value ?? defaultEpiOffset;
  internalEpiOffset.value = Math.max(0, Math.min(100, current + delta));
  applyEndoEpiOffsets();
}

function stepEndo(delta: number) {
  const current = internalEndoOffset.value ?? defaultEndoOffset;
  internalEndoOffset.value = Math.max(0, Math.min(100, current + delta));
  applyEndoEpiOffsets();
}

function onEpiWheel(event: WheelEvent) {
  const delta = event.deltaY < 0 ? 5 : -5;
  stepEpi(delta);
}

function onEndoWheel(event: WheelEvent) {
  const delta = event.deltaY < 0 ? 5 : -5;
  stepEndo(delta);
}

function handleOffsetChange() {
  applyEndoEpiOffsets();
}

const isOffsetModified = computed(() =>
  internalEpiOffset.value !== defaultEpiOffset
  || internalEndoOffset.value !== defaultEndoOffset,
);

function handleResetOffsets() {
  resetAppOffsets();
}

const activeViewers = computed(() => appState.activeViewers);

const overlayTools = [
  {
    icon: 'mdi mdi-dock-right',
    label: 'Series Browser',
    onClick: () => {
      if (
        generalPurposeOverlayState.isOpen
        && generalPurposeOverlayState.viewerType === 'viewer'
      ) {
        overlayManager.closeGeneralPurposeOverlay();
        return;
      }

      overlayManager.showGeneralPurposeOverlay({ viewerType: 'viewer' });
    },
  },
  {
    icon: 'mdi mdi-clipboard-outline',
    label: 'Image Clipboard',
    onClick: () => {
      if (
        generalPurposeOverlayState.isOpen
        && generalPurposeOverlayState.viewerType === 'clipboard'
      ) {
        overlayManager.closeGeneralPurposeOverlay();
        return;
      }

      overlayManager.showGeneralPurposeOverlay({ viewerType: 'clipboard' });
    },
  },
  {
    icon: 'mdi mdi-folder-zip-outline',
    label: 'Export Segmentation',
    onClick: () => {
      if (
        generalPurposeOverlayState.isOpen
        && generalPurposeOverlayState.viewerType === 'extractor'
      ) {
        overlayManager.closeGeneralPurposeOverlay();
      }
      else {
        overlayManager.showGeneralPurposeOverlay({ viewerType: 'extractor' });
      }
    },
  },
];

const aiTools = [
  {
    icon: 'mdi mdi-image-filter-tilt-shift',
    name: 'AutoSegmentation',
    label: 'Segment Images',

    onClick: () => {
      if (segModule.value?.isSegmentationActive) {
        segModule.value?.abortSegmentation();
        return;
      }
      segModule.value?.segmentActiveViewers();
    },
  },
  {
    icon: '/icons/analysis_icon_draft.svg',
    name: 'OCGAnalysis',
    label: 'Run Analysis',
    onClick: () => {
      if (ocgModule.value?.isAnalysisActive) {
        ocgModule.value?.abortAnalysis();
        return;
      }
      ocgModule.value?.beginAnalysis();
    },
  },
];

// Computed properties for filtered tools
const visibleAiTools = computed(() =>
  aiTools.filter(tool =>
    viewportStateStore.shouldShowToolByAppView(tool.name, appState.currentView),
  ),
);

const shouldShowOffsetTools = computed(() =>
  viewportStateStore.shouldShowToolByAppView(
    'OffsetSettings',
    appState.currentView,
  ),
);

// Methods
function toggleCategory(category: keyof typeof openCategories.value) {
  openCategories.value[category] = !openCategories.value[category];
  Object.keys(openCategories.value).forEach((key) => {
    if (key !== category) {
      openCategories.value[key as keyof typeof openCategories.value] = false;
    }
  });
}

// const aiToolsCategory = {
//   icon: 'mdi mdi-creation',
//   name: 'AI Tools',
// };

const offsetToolsCategory = {
  icon: '/icons/offset_contour_draft.svg',
  name: 'Offset %',
};

const contourToolsCategory = {
  icon: 'mdi mdi-draw-pen',
  name: 'Contour Tools',
};

const cameraControlCategory = {
  icon: 'mdi mdi-camera-control',
  name: 'Camera Controls',
};

const newContourTools = [
  {
    label: 'SAX Endo',
    icon: ContourLabelToIcon[ContourLabel.SaxLvEndo],
    onClick: () => {
      viewportActions.setDrawingContour(
        activeViewers.value[0],
        ContourLabel.SaxLvEndo,
      );
    },
  },
  {
    label: 'SAX Epi',
    icon: ContourLabelToIcon[ContourLabel.SaxLvEpi],
    onClick: () => {
      viewportActions.setDrawingContour(
        activeViewers.value[0],
        ContourLabel.SaxLvEpi,
      );
    },
  },
  {
    label: 'RV Insertion Point',
    icon: ContourLabelToIcon[ContourLabel.RvInsertion],
    onClick: () => {
      viewportActions.setDrawingContour(
        activeViewers.value[0],
        ContourLabel.RvInsertion,
      );
    },
  },
  {
    label: 'SAX RV Endo',
    icon: ContourLabelToIcon[ContourLabel.SaxRvEndo],
    onClick: () => {
      viewportActions.setDrawingContour(
        activeViewers.value[0],
        ContourLabel.SaxRvEndo,
      );
    },
  },
  {
    label: 'LAX Endo',
    icon: ContourLabelToIcon[ContourLabel.LaxLvEndo],
    onClick: () => {
      viewportActions.setDrawingContour(
        activeViewers.value[0],
        ContourLabel.LaxLvEndo,
      );
    },
  },
  {
    label: 'LAX Epi',
    icon: ContourLabelToIcon[ContourLabel.LaxLvEpi],
    onClick: () => {
      viewportActions.setDrawingContour(
        activeViewers.value[0],
        ContourLabel.LaxLvEpi,
      );
    },
  },
  {
    label: 'Freehand',
    icon: ContourLabelToIcon[ContourLabel.GeneralPolygon],
    onClick: () => {
      viewportActions.setDrawingContour(
        activeViewers.value[0],
        ContourLabel.GeneralPolygon,
      );
    },
  },
  {
    label: 'Distance',
    icon: ContourLabelToIcon[ContourLabel.GeneralLine],
    onClick: () => {
      viewportActions.setDrawingContour(
        activeViewers.value[0],
        ContourLabel.GeneralLine,
      );
    },
  },
  {
    label: 'Annotation Text',
    icon: ContourDefinitions[ContourLabel.AnnotationText].icon,
    onClick: () => {
      viewportActions.setDrawingContour(
        activeViewers.value[0],
        ContourLabel.AnnotationText,
      );
    },
  },
  {
    label: 'Annotation Arrow',
    icon: ContourDefinitions[ContourLabel.AnnotationArrow].icon,
    onClick: () => {
      viewportActions.setDrawingContour(
        activeViewers.value[0],
        ContourLabel.AnnotationArrow,
      );
    },
  },
  {
    label: 'Mask Measurement',
    icon: ContourDefinitions[ContourLabel.MaskMeasurement].icon,
    onClick: () => {
      viewportActions.setDrawingContour(
        activeViewers.value[0],
        ContourLabel.MaskMeasurement,
      );
    },
  },
];

const cameraControlTools = [
  {
    label: 'Pan',
    icon: '/icons/image-interaction/panning.svg',
    onClick: () => {
      viewportStateStore.setToolMode(
        activeViewers.value[0],
        ViewportToolMode.Pan,
      );
    },
  },
  {
    label: 'Zoom',
    icon: '/icons/image-interaction/zooming.svg',
    onClick: () => {
      viewportStateStore.setToolMode(
        activeViewers.value[0],
        ViewportToolMode.Zoom,
      );
    },
  },
  {
    label: 'Window/Level',
    icon: '/icons/image-interaction/windowing.svg',
    onClick: () => {
      viewportStateStore.setToolMode(
        activeViewers.value[0],
        ViewportToolMode.WindowLevel,
      );
    },
  },
];

async function handleSaveAnalysisStateClick() {
  if (isSavingAnalysisState.value) {
    return;
  }

  isSavingAnalysisState.value = true;
  try {
    await loadSaveAnalysisStateDialog.value?.backgroundAutosave(
      currentStudyStore.getStudyID,
    );
    alert.info('Your analysis state has been saved.');
  }
  finally {
    isSavingAnalysisState.value = false;
  }
}

function handleSaveAnalysisStateContextMenu() {
  loadSaveAnalysisStateDialog.value?.invokeSave(currentStudyStore.getStudyID);
}

function handleClickTool(tool: any) {
  if (tool.onClick) {
    tool.onClick();
  }
}
</script>

<template>
  <div class="simple-panel flex align-center gap-2 h-[40px] py-2">
    <!-- Contour Offset Control -->
    <div v-if="shouldShowOffsetTools" class="tool-category">
      <ToolButton
        :size="40"
        :icon="offsetToolsCategory.icon"
        :label="offsetToolsCategory.name"
        :class="{ 'category-open': openCategories.offsetTools }"
        tooltip-location="bottom"
        @click="toggleCategory('offsetTools')"
      />
      <transition name="slide-fade">
        <div v-if="openCategories.offsetTools" class="category-tools">
          <div class="flex items-center gap-1.5 offset-container">
            <!-- EPI Stepper -->
            <div class="offset-control-group flex items-center">
              <span class="offset-badge epi-badge">
                EPI
              </span>
              <button
                type="button"
                class="offset-step-btn"
                :disabled="internalEpiOffset <= 0"
                title="Decrease EPI offset by 5%"
                aria-label="Decrease EPI offset"
                @click="stepEpi(-5)"
              >
                <v-icon size="14">
                  mdi-minus
                </v-icon>
              </button>
              <div class="offset-select-wrapper" @wheel.prevent="onEpiWheel">
                <select
                  v-model.number="internalEpiOffset"
                  class="offset-select"
                  title="EPI offset (scroll to adjust)"
                  @change="handleOffsetChange"
                >
                  <option v-for="val in offsetOptions" :key="val" :value="val">
                    {{ val }}%
                  </option>
                </select>
                <v-icon size="12" class="offset-select-arrow">
                  mdi-chevron-down
                </v-icon>
              </div>
              <button
                type="button"
                class="offset-step-btn"
                :disabled="internalEpiOffset >= 100"
                title="Increase EPI offset by 5%"
                aria-label="Increase EPI offset"
                @click="stepEpi(5)"
              >
                <v-icon size="14">
                  mdi-plus
                </v-icon>
              </button>
            </div>

            <!-- ENDO Stepper -->
            <div class="offset-control-group flex items-center">
              <span class="offset-badge endo-badge">
                ENDO
              </span>
              <button
                type="button"
                class="offset-step-btn"
                :disabled="internalEndoOffset <= 0"
                title="Decrease ENDO offset by 5%"
                aria-label="Decrease ENDO offset"
                @click="stepEndo(-5)"
              >
                <v-icon size="14">
                  mdi-minus
                </v-icon>
              </button>
              <div class="offset-select-wrapper" @wheel.prevent="onEndoWheel">
                <select
                  v-model.number="internalEndoOffset"
                  class="offset-select"
                  title="ENDO offset (scroll to adjust)"
                  @change="handleOffsetChange"
                >
                  <option v-for="val in offsetOptions" :key="val" :value="val">
                    {{ val }}%
                  </option>
                </select>
                <v-icon size="12" class="offset-select-arrow">
                  mdi-chevron-down
                </v-icon>
              </div>
              <button
                type="button"
                class="offset-step-btn"
                :disabled="internalEndoOffset >= 100"
                title="Increase ENDO offset by 5%"
                aria-label="Increase ENDO offset"
                @click="stepEndo(5)"
              >
                <v-icon size="14">
                  mdi-plus
                </v-icon>
              </button>
            </div>

            <!-- Reset to Defaults Button -->
            <v-tooltip location="bottom">
              <template #activator="{ props: tooltipProps }">
                <button
                  v-bind="tooltipProps"
                  type="button"
                  class="offset-reset-btn"
                  :class="{ 'is-active': isOffsetModified }"
                  :disabled="!isOffsetModified"
                  aria-label="Reset offsets to defaults"
                  @click="handleResetOffsets"
                >
                  <v-icon size="16">
                    mdi-restore
                  </v-icon>
                </button>
              </template>
              <span>Reset defaults (EPI: 10%, ENDO: 15%)</span>
            </v-tooltip>
          </div>
        </div>
      </transition>
    </div>
    <div v-if="shouldShowOffsetTools" class="vertical-divider" />

    <!-- AI Tools Category -->
    <div v-if="visibleAiTools.length > 0" class="tool-category">
      <div class="category-tools">
        <ToolButton
          v-for="(tool, index) in visibleAiTools"
          :key="index"
          :size="40"
          :icon="tool.icon"
          :label="tool.label"
          tooltip-location="bottom"
          @click="() => handleClickTool(tool)"
        />
      </div>
    </div>

    <div v-if="visibleAiTools.length > 0" class="vertical-divider" />

    <!-- Contour Tools Category -->
    <div class="tool-category">
      <ToolButton
        :size="40"
        :icon="contourToolsCategory.icon"
        :label="contourToolsCategory.name"
        :class="{ 'category-open': openCategories.contourTools }"
        tooltip-location="bottom"
        @click="toggleCategory('contourTools')"
      />
      <transition name="slide-fade">
        <div v-if="openCategories.contourTools" class="category-tools">
          <ToolButton
            v-for="(tool, index) in newContourTools"
            :key="index"
            :size="40"
            :icon="tool.icon ?? ''"
            :label="tool.label"
            tooltip-location="bottom"
            @click="() => handleClickTool(tool)"
          />
        </div>
      </transition>
    </div>

    <div class="vertical-divider" />

    <!-- Camera Control Category -->
    <div class="tool-category">
      <ToolButton
        :size="40"
        :icon="cameraControlCategory.icon"
        :label="cameraControlCategory.name"
        :class="{ 'category-open': openCategories.cameraControl }"
        tooltip-location="bottom"
        @click="toggleCategory('cameraControl')"
      />
      <transition name="slide-fade">
        <div v-if="openCategories.cameraControl" class="category-tools">
          <ToolButton
            v-for="(tool, index) in cameraControlTools"
            :key="index"
            :size="40"
            :icon="tool.icon"
            :label="tool.label"
            tooltip-location="bottom"
            @click="() => handleClickTool(tool)"
          />
        </div>
      </transition>
    </div>
    <div class="vertical-divider" />
    <div class="category-tools">
      <ToolButton
        v-for="(tool, index) in overlayTools"
        :key="index"
        :size="40"
        :icon="tool.icon"
        :label="tool.label"
        tooltip-location="bottom"
        @click="tool.onClick"
      />
    </div>

    <div class="vertical-divider" />
    <ToolButton
      :size="40"
      icon="mdi mdi-arrow-u-left-top"
      label="Undo"
      tooltip-location="bottom"
      :disabled="!viewportActions.hasPast()"
      @click="viewportActions.handleUndo"
    />
    <ToolButton
      :size="40"
      icon="mdi mdi-arrow-u-right-top"
      label="Redo"
      tooltip-location="bottom"
      :disabled="!viewportActions.hasFuture()"
      @click="viewportActions.handleRedo"
    />
    <div class="vertical-divider" />
    <div class="category-tools">
      <ToolButton
        :size="40"
        icon="mdi mdi-content-save-outline"
        label="Save Analysis State"
        :loading="isSavingAnalysisState"
        tooltip-location="bottom"
        @click="handleSaveAnalysisStateClick"
        @contextmenu.prevent="handleSaveAnalysisStateContextMenu"
      />
      <ToolButton
        :size="40"
        icon="mdi mdi-cloud-download-outline"
        label="Load Analysis State"
        tooltip-location="bottom"
        @click="
          () =>
            loadSaveAnalysisStateDialog?.invokeLoad(
              currentStudyStore.getStudyID,
            )
        "
      />
    </div>
  </div>
  <SegmentationModule ref="segModule" />
  <OCGModule
    ref="ocgModule"
    :show-dialog="showOcgDialog"
    @update:show-dialog="showOcgDialog = $event"
  />
  <LoadSaveAnalysisStateDialog ref="loadSaveAnalysisStateDialog" />
</template>

<style scoped>
.tool-category {
  display: flex;
  align-items: center;
  position: relative;
}

.category-tools {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-left: 4px;
}

/* Category button visual states */
.category-open {
  filter: brightness(1.2);
}

/* Slide animation */
.slide-fade-enter-active {
  transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.slide-fade-leave-active {
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.slide-fade-enter-from {
  transform: translateX(-20px) scale(0.9);
  opacity: 0;
}

.slide-fade-leave-to {
  transform: translateX(-10px) scale(0.95);
  opacity: 1;
}

/* Slide in from right animation for GeneralPurposeContainer */
.slide-right-fade-enter-active,
.slide-right-fade-leave-active {
  transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
}
.slide-right-fade-enter-from {
  transform: translateX(40px);
  opacity: 0;
}
.slide-right-fade-leave-to {
  transform: translateX(40px);
  opacity: 0;
}

.tool-buttons-container {
  display: flex;
  flex-direction: column;
  position: relative;
}

.offset-container {
  display: flex;
  align-items: center;
  gap: 6px;
}

.offset-control-group {
  display: flex;
  align-items: center;
  height: 28px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid var(--md-outline-variant, rgba(255, 255, 255, 0.15));
  border-radius: 6px;
  padding: 1px 3px;
  gap: 2px;
}

.offset-badge {
  display: flex;
  align-items: center;
  gap: 3px;
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.5px;
  padding: 0 4px;
  user-select: none;
}

.epi-badge {
  color: #0ecf00;
}

.endo-badge {
  color: #ff0000;
}

.offset-dot {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  display: inline-block;
}

.offset-step-btn {
  width: 20px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  cursor: pointer;
  color: var(--md-on-surface, #e0e0e0);
  border-radius: 4px;
  transition:
    background-color 0.15s ease,
    color 0.15s ease;
}

.offset-step-btn:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
}

.offset-step-btn:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.offset-select-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.offset-select {
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  background: transparent;
  border: none;
  outline: none;
  color: #fff;
  font-size: 0.75rem;
  font-weight: 600;
  font-family: inherit;
  padding: 0 16px 0 4px;
  height: 24px;
  cursor: pointer;
  text-align: right;
  text-align-last: right;
}

.offset-select option {
  background: #1e1e1e;
  color: #fff;
}

.offset-select-arrow {
  position: absolute;
  right: 2px;
  pointer-events: none;
  opacity: 0.6;
}

.offset-reset-btn {
  width: 28px;
  height: 28px;
  border: 1px solid transparent;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  color: var(--md-on-surface-variant, rgba(255, 255, 255, 0.4));
  cursor: pointer;
  transition: all 0.2s ease;
}

.offset-reset-btn:disabled {
  opacity: 0.25;
  cursor: default;
}
</style>
